import { translateBackend } from 'assets/i18n/config';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { kebabCase } from 'lodash';
import {
  Grid, TextField, Typography,
} from '../../../..';
import { UPDATE_INTEGRATION_FORM_AGREEMENT } from './collectSigners';
import { validEmail } from '../utils';
import { useGlobalToast } from '../../../../../providers/globalToastProvider';
import { WorkflowCompletionForm } from '../../components/workflowCompletionForm';

interface SignerInfo {
  firstName: string;
  lastName: string;
  email: string;
}

interface SignerRole {
  roleId: string;
  roleName: string;
  roleType: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}
interface CollectSignersVisualProps {
  options: any;
  externalId: string;
  signingRoles: SignerRole[];
  signerInfo: { [roleId: string]: SignerInfo };
  setSignerInfo: (updater: (prev: any) => any) => void;
  loading: boolean;
  onNext: () => void;
}

const isValidEmail = (email?: string): boolean => {
  if (!email) return true;
  return validEmail(email.trim()); // use util regex
};

// Ensure that the signer information is complete and the email is valid.
const isSignerInfoValid = (info?: SignerInfo): boolean => Boolean(info?.firstName && info?.lastName && info?.email && isValidEmail(info.email));

export const CollectSignersVisual = ({
  options, externalId, signingRoles, signerInfo, setSignerInfo, loading, onNext,
}: CollectSignersVisualProps) => {
  const { t } = useTranslation('client');
  const { showToast } = useGlobalToast();

  const [sendIntegrationFormAgreement, { error: mutationError }] = useMutation(UPDATE_INTEGRATION_FORM_AGREEMENT);

  const handleChange = (roleId: string, field: keyof SignerInfo, value: string) => {
    setSignerInfo((prev: any) => ({
      ...prev,
      [roleId]: {
        ...prev[roleId],
        [field]: value,
      },
    }));
  };

  const submit = async () => {
    const hasPartialInput = signingRoles.some((role) => {
      const info = signerInfo[role.roleId];
      const anyField = info?.firstName || info?.lastName || info?.email;
      const allFields = info?.firstName && info?.lastName && info?.email;
      return Boolean(anyField && !allFields);
    });

    if (hasPartialInput) {
      showToast({ message: 'Incomplete information has been entered for a signer.', severity: 'error' });
      return;
    }

    const inputtedSigners = signingRoles
      .filter((role) => isSignerInfoValid(signerInfo[role.roleId]))
      .map((role) => ({
        ...role,
        ...signerInfo[role.roleId],
      }));

    await sendIntegrationFormAgreement({
      variables: {
        input: {
          signers: inputtedSigners,
          externalId,
        },
      },
    });

    if (mutationError) {
      // eslint-disable-next-line no-console
      console.log('Error sending integration form agreement:', mutationError);
    } else await onNext();
  };

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('confirmationDialog.continueButton'), disabled: loading, dataTestId: `collect-signers-${kebabCase(options?.title?.en)}-continue-button`,
      }}
    >
      <Typography variant='displayLarge' sx={{ mt: 1 }}>
        {translateBackend(options?.title)}
      </Typography>
      <Typography variant='bodyLarge' sx={{ mb: 3 }}>
        {translateBackend(options?.subtitle)}
      </Typography>
      <Grid container spacing={2}>
        {signingRoles?.map(
          (role: any) => role && (
              <Grid item xs={12} md={12} key={role.roleId}>
                <Typography variant='labelLarge' sx={{ mb: 2 }}>
                  {t(role.roleName)}
                </Typography>
                <TextField
                  testId={`collect-signers-${kebabCase(role.roleName)}-first-name`}
                  label={t('details.firstName')}
                  onChange={(e: any) => handleChange(role.roleId, 'firstName', e.target.value)}
                  value={signerInfo[role.roleId]?.firstName ?? role.firstName ?? ''}
                  onBlur={undefined}
                  fullWidth
                  sx={{ mb: 2 }}
                  disabled={loading}
                />
                <TextField
                  testId={`collect-signers-${kebabCase(role.roleName)}-last-name`}
                  label={t('details.lastName')}
                  onChange={(e: any) => handleChange(role.roleId, 'lastName', e.target.value)}
                  value={signerInfo[role.roleId]?.lastName ?? role.lastName ?? ''}
                  onBlur={undefined}
                  fullWidth
                  sx={{ mb: 2 }}
                  disabled={loading}
                />
                <TextField
                  testId={`collect-signers-${kebabCase(role.roleName)}-primary-email`}
                  label={t('details.primaryEmail')}
                  onChange={(e: any) => handleChange(role.roleId, 'email', e.target.value)}
                  value={signerInfo[role.roleId]?.email ?? role.email ?? ''}
                  fullWidth
                  sx={{ mb: 2 }}
                  disabled={loading}
                  error={!isValidEmail(signerInfo[role.roleId]?.email)}
                />
              </Grid>
          ),
        )}
      </Grid>
    </WorkflowCompletionForm>
  );
};
